/* eslint-disable complexity */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { Injectable } from '@angular/core';
import { CrewStore } from '@flight-details/crew/crew.store';
import { DataItem } from '@shared/models/data-item';
import { RequestState, RequestStatus } from '@shared/models/request-state';
import { MelView } from '@shared/models/mel.view';
// import { AircraftTypeService } from '@shared/services/aircraft-type.service';
import { map, Observable, of, switchMap } from 'rxjs';
import { AircraftService } from './aircraft.service';
import {
  AircraftLeg,
  AircraftRoutingView,
} from './models/aircraft-routing.view';
import { AlertService } from '@shared/services/alert/alert.service';
import { DecsResponse } from '@shared/services/decs/models/response/decs.response';
import { LegView } from '@sequence/models/view/leg.view';
import { AircraftDetailsView } from './models/aircraft-details.view';

@Injectable({
  providedIn: 'root',
})
export class AircraftStore {
  constructor(
    private aircraftService: AircraftService,
    private crewStore: CrewStore,
    // private aircraftTypeService: AircraftTypeService,
    private alertService: AlertService,
  ) {}

  /** Get aircraft number and type. */
  getAircraftDetails(
    legView: LegView,
    isRefresh = false,
  ): Observable<RequestState<AircraftDetailsView>> {
    const crewRequest = legView.getCrewRequest();

    if (!crewRequest) {
      return of(
        new RequestState<AircraftDetailsView>(
          RequestStatus.IsCompleteNoResponse,
        ),
      );
    }

    return this.crewStore.getCrew(crewRequest, isRefresh).pipe(
      map((crewRequestState) => {
        if (crewRequestState.hasError) {
          this.showErrorAlert('Failed to load aircraft information.');
        }

        // Pass on loading/error state.
        if (!crewRequestState.response) {
          return crewRequestState.into<AircraftDetailsView>();
        }

        const aircraftDetails = this.parseAircraftDetails(
          crewRequestState.response.gate.raw,
        );

        const dataItems = this.mapDetailsDataItems(
          aircraftDetails,
          legView.aircraft.long,
        );

        return crewRequestState.into<AircraftDetailsView>({
          timestamp: crewRequestState.response.timestamp,
          aircraftNumber: aircraftDetails?.number || '',
          dataItems,
        });
      }),
    );
  }

  /** Update MELs when selectedFlightCrew changes. */
  getMels(
    legView: LegView,
    isRefresh = false,
  ): Observable<RequestState<MelView[]>> {
    const crewRequest = legView.getCrewRequest();

    if (!crewRequest) {
      return of(
        new RequestState<MelView[]>(RequestStatus.IsCompleteNoResponse),
      );
    }

    return this.crewStore.getCrew(crewRequest, isRefresh).pipe(
      switchMap((crewState) => {
        if (!crewState.response) {
          return of(crewState.into<DecsResponse>());
        }

        const aircraftDetails = this.parseAircraftDetails(
          crewState.response.gate.raw,
        );

        if (!aircraftDetails || !aircraftDetails.number) {
          return of(crewState.into<DecsResponse>());
        }

        return this.aircraftService.getMels(aircraftDetails.number, isRefresh);
      }),
      map((sabreResponseState: RequestState<DecsResponse>) => {
        if (sabreResponseState.hasError) {
          this.showErrorAlert('Failed to load MELs.');
        }

        if (!sabreResponseState.response) {
          return sabreResponseState.into<MelView[]>();
        }

        // Parse mels.
        const mels = this.parseMels(sabreResponseState.response.result);

        return sabreResponseState.into<MelView[]>(mels);
      }),
    );
  }

  /**
   * Parse aircraft number and type from first line of NS text.
   * Ex: 271/14 26 N426  FROM 298/13 IN 0735/14 AS OF 14FEB23/1203
   *            ^^ ^^^^
   */
  private parseAircraftDetails(
    text?: string,
  ): { type: string; number: string } | null {
    if (!text) {
      return null;
    }

    const lines = text.split('\n');
    if (lines.length < 3) {
      null;
    }

    const re = /^\S{1,4}\/\d{2} (?<type>\S{1,2})\s+N(?<num>\S{3})\s+FROM/;
    const match = lines[0].match(re);

    if (match && match.groups?.['num'] && match.groups?.['type']) {
      return {
        type: match.groups['type'],
        number: match.groups['num'],
      };
    } else {
      return null;
    }
  }

  /** Generate DataItem array for displaying aircraft details. */
  private mapDetailsDataItems(
    details: {
      type: string;
      number: string;
    } | null,
    acftDescription: string,
  ): DataItem[] {
    if (!details) {
      return [];
    }

    return [
      {
        label: 'Type',
        value: acftDescription,
      },
      { label: 'Nose', value: details.number },
    ];
  }

  /**
   * Parse Sabre MEL list. Returns array of MelViews. Parses type, number and description.
   * Ex: MEL.NBR          DSP MAN REF PLACARDED BY   FMR  DAYOPN CLSD BY
   *     ---------------------------------------------------------------
   *
   *     7BP  PLND  SJU  1671/14FEB     DFW  2196/15FEB
   *     0-510D-C             11-99B          NON FAA REQ PLAC
   *     1343T00              11T99B HDQ TRIP               *001*  OPEN
   *                          OPEN 13FEB/1533
   *
   *     0-509D-C             22-89A          LMP NO LAND 3
   *     1312N13              22 89A HDQ TRIP               *001*  OPEN
   *                          OPEN 13FEB/1343
   *
   *     0-508D-C             34-17           RADIO ALTIMETER
   *     1310B16              34 17  HDQ TRIP               *001*  OPEN
   *                          OPEN 13FEB/1337
   *
   *     END
   */
  private parseMels(text: string): MelView[] {
    // Column constants
    const MEL_NUM_START = 21;
    const MEL_NUM_LENGTH = 33;
    const DESCRIPTION_START = 37;
    const melIdRe = /^\d{4}(?<idLetter>[A-Z]{1})\d{2}/;

    // Find all pertinent lines and collect in array.
    // Then take the two lines associated with the MEL and parse.
    const lines = text.split('\n');
    const mels = [];

    for (let i = 0; i < lines.length; i++) {
      // Row with MEL number and description always starts with 0-
      if (lines[i].startsWith('0-') && lines[i].length > DESCRIPTION_START) {
        const melNumber = lines[i]
          .substring(MEL_NUM_START, MEL_NUM_LENGTH)
          .trim();

        const description = lines[i].substring(DESCRIPTION_START).trim();

        let type = 'MEL';

        // Look for NEF within start ID: 0-413NEF-C
        if (lines[i].substring(0, MEL_NUM_START).includes('NEF')) {
          type = 'NEF';
        }

        // Look for CDL within start ID: 0-648CDL-C
        if (lines[i].substring(0, MEL_NUM_START).includes('CDL')) {
          type = 'CDL';
        }

        const melView: MelView = {
          type,
          id: melNumber,
          description,
          modifier: '',
        };

        mels.push(melView);
      } else if (lines[i].match(melIdRe)?.groups?.['idLetter'] === 'T') {
        // 'T' in second row ID indicates type is TAC. Change the last MEL in the array to TAC type.
        if (mels.length > 0) {
          mels[mels.length - 1].type = 'TAC';
        }
      }
    }

    return mels;
  }

  /** Get aircraft routing for the current day. */
  getRouting(
    legView: LegView,
    isRefresh = false,
  ): Observable<RequestState<AircraftRoutingView>> {
    const crewRequest = legView.getCrewRequest();

    if (!crewRequest) {
      return of(
        new RequestState<AircraftRoutingView>(
          RequestStatus.IsCompleteNoResponse,
        ),
      );
    }

    return this.crewStore.getCrew(crewRequest, isRefresh).pipe(
      switchMap((crewState) => {
        if (!crewState.response) {
          return of(crewState.into<DecsResponse>());
        }

        const aircraftNumber = this.parseAircraftDetails(
          crewState.response.gate.raw,
        )?.number;

        if (!aircraftNumber) {
          return of(crewState.into<DecsResponse>());
        }

        return this.aircraftService.getRouting(aircraftNumber);
      }),
      map((aircraftRoutingState) => {
        if (aircraftRoutingState.hasError) {
          this.showErrorAlert('Failed to load aircraft routing information.');
        }

        if (!aircraftRoutingState.response) {
          return aircraftRoutingState.into<AircraftRoutingView>();
        }

        const routing = this.parseRouting(aircraftRoutingState.response.result);

        return aircraftRoutingState.into<AircraftRoutingView>(routing);
      }),
    );
  }

  /**
   * Parse aircraft routing text. On error, it will provide an empty legs list.
   *
   *  FLT --FROM--     --TO--     MTC  -OFF/ON-- TIME    TST    CYCL
   * 2357/21 DFW 0858 DEN 0940 907    0858 0940  1.42 17348.56  6688
   * 2357/21 DEN 1122 DFW 1352 907    1122            17351.04  6689
   * 2640/21 DFW 1500 PBI 1815 907XX                  17353.40  6690
   *           1         2         3         4         5         6
   * 0123456789012345678901234567890123456789012345678901234567890123456789
   */
  private parseRouting(text: string): AircraftRoutingView {
    const legs: AircraftLeg[] = [];
    const minLineLength = 61;
    const isFlightLineRe = /^\s*\d{1,4}\/\d{2}/; // Match a line that starts like [123]4/01.

    for (const line of text.split('\n')) {
      if (isFlightLineRe.test(line) && line.length > minLineLength) {
        // Columns are static, use column indices to parse data.
        const flightNumber = line.slice(0, 4).trim();
        const day = line.slice(5, 7).trim();
        const from = line.slice(8, 11).trim();
        const to = line.slice(17, 20).trim();
        const depTime = `${line.slice(12, 14).trim()}:${line
          .slice(14, 16)
          .trim()}`;
        const arrTime = `${line.slice(21, 23).trim()}:${line
          .slice(23, 25)
          .trim()}`;
        const off = line.slice(33, 37).trim();
        const on = line.slice(38, 42).trim();
        const mtc = line.slice(29, 31).trim();

        let status: 'landed' | 'scheduled' | 'in flight' = 'scheduled';
        if (off !== '') {
          status = 'in flight';
          if (on !== '') {
            status = 'landed';
          }
        }

        legs.push({
          flightNumber,
          day,
          from,
          to,
          depTime,
          arrTime,
          status,
        });

        // Stop parsing if XX encountered under MTC column and this isn't the first flight listed.
        // An XX flight will be the first flight when flight is starting at overnight station.
        if (mtc === 'XX' && legs.length > 1) {
          break;
        }
      }
    }

    return {
      legs,
    };
  }

  private showErrorAlert(message: string): void {
    this.alertService.setAlert({
      type: 'error',
      message,
    });
  }
}
